import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  Drawer,
  DrawerHeader,
  DrawerFooter,
  DrawerBody,
  Address
} from '@cat-ecom/pcc-components';
import { CatButton } from '@blocks-react/components/Button';
import { CatHeading } from '@blocks-react/components/Heading';
import { CatRadioField } from '@blocks-react/components/RadioField';
import { CatIconArrowBack } from '@blocks-react/components/Icons/ArrowBack';
import Conditional from '../../Conditional';
import InlineNotification from '../../Blocks/InlineNotification';
import { SELECTED_ADDRESS } from '../constants';
import { useSystemFeedback } from '@app/hooks';
import {
  ERR_NETWORK,
  ERROR_DOMAIN,
  ERROR_PATH
} from '@app/constants/errorConstants';
import { SHOP_ONLINE } from '@app/components/pages/checkout/PickUpAndDelivery/constants';
import { STATUS } from '@app/constants/commonConstants';
import styles from './AVIMobileDrawer.module.scss';

const AVIMobileDrawer = ({
  isActive,
  current,
  onSubmit,
  aviAddressData = null,
  errorSelector = ERROR_DOMAIN.CHECKOUT,
  errorPath = ERROR_PATH.MODAL,
  ...AVIMobileDrawerProps
}) => {
  const [t] = useTranslation();
  const isSubmitTriggered = useRef(false);
  const { clearError } = useSystemFeedback();
  const locale = useSelector(state => state.common?.locale);
  const suggestedAddressState = useSelector(state => state.checkout?.avi);
  const suggested =
    aviAddressData?.suggestedAddress || suggestedAddressState || {};
  const isAviCallSuccess = useSelector(
    state => state.checkout.isAviCallSuccess
  );
  const saveAddressStatus = useSelector(
    state => state.checkout[SHOP_ONLINE].saveAddressStatus
  );
  const verifyAddressError = useSelector(
    state => state.errors[errorSelector]?.[errorPath]
  );
  const { required } = suggested || {};
  const suggestedStr = SELECTED_ADDRESS.SUGGESTED;
  const { address1, address2, city, state, zipCode, country } =
    aviAddressData?.currentAddress || current || {};
  const {
    closeDrawer,
    AddAddressDrawerCloseHandler,
    setSelected: setAddressSelected
  } = AVIMobileDrawerProps;
  const currentAddress = {
    address1,
    address2,
    city,
    state,
    zipCode,
    country
  };

  const [selected, setSelected] = useState(SELECTED_ADDRESS.SUGGESTED);

  const suggestedAddress = { ...current, ...suggested };
  const selectedAddress =
    selected === suggestedStr ? suggestedAddress : current;

  const onContinueHandler = () => {
    onSubmit({
      isSuggestedAddressSelected: selected === suggestedStr,
      ...selectedAddress
    });
    clearError(errorSelector, errorPath);
    isSubmitTriggered.current = true;
  };

  useEffect(() => {
    if (
      isSubmitTriggered.current &&
      !verifyAddressError &&
      (isAviCallSuccess || saveAddressStatus === STATUS.RESOLVED)
    ) {
      isSubmitTriggered.current = false;
      setAddressSelected(selectedAddress);
      closeDrawer();
      AddAddressDrawerCloseHandler();
    }
  }, [
    AddAddressDrawerCloseHandler,
    closeDrawer,
    isAviCallSuccess,
    saveAddressStatus,
    selectedAddress,
    setAddressSelected,
    verifyAddressError
  ]);

  const getErrorMessage = useCallback(() => {
    let errorMessage = t('UNKONWN_ERROR');
    if (verifyAddressError?.message) {
      errorMessage = Array.isArray(verifyAddressError?.message)
        ? verifyAddressError?.message
        : t(verifyAddressError?.message);
    }
    return `${errorMessage} (${verifyAddressError?.code ?? ERR_NETWORK})`;
  }, [t, verifyAddressError?.code, verifyAddressError?.message]);

  return (
    <Drawer {...AVIMobileDrawerProps} isActive={isActive}>
      <DrawerHeader>
        <div className="d-flex align-items-center">
          <CatButton
            hideText
            variant="ghost"
            className="me-3"
            data-testid="go-back-button"
            size="sm"
            onClick={AVIMobileDrawerProps.closeDrawer}
          >
            <CatIconArrowBack slot="before" />
          </CatButton>
          <h4
            className={cx(
              'cat-u-theme-typography-title m-0 text-capitalize',
              styles['avi-mobile__title']
            )}
          >
            {t('AVI_LABEL')}
          </h4>
        </div>
      </DrawerHeader>
      <DrawerBody>
        <div className="d-flex flex-column gap-3">
          <Conditional test={verifyAddressError}>
            <InlineNotification
              customClasses={styles['avi-mobile__inline-notification']}
              title={
                verifyAddressError?.title
                  ? t(verifyAddressError?.title)
                  : t('SORRY_ERROR_OCCURRED')
              }
              iconColor="content-strong"
            >
              <div className="mt-1">{getErrorMessage()}</div>
            </InlineNotification>
          </Conditional>
          <CatHeading variant="body">{t('AVI_ADDRESS_NOT_FOUND')}</CatHeading>
          {current && !required ? (
            <>
              <div className="px-2 py-3">
                <CatRadioField
                  checked={selected === SELECTED_ADDRESS.SUGGESTED}
                  onBlRadioChange={() =>
                    setSelected(SELECTED_ADDRESS.SUGGESTED)
                  }
                >
                  <div
                    className={cx(
                      'd-flex flex-column',
                      styles['avi-mobile__address']
                    )}
                  >
                    <p className="mb-0">{t('ES_RECOMMENDATIONS')}</p>
                    <Address locale={locale} address={suggested} />
                  </div>
                </CatRadioField>
              </div>
              <div className="px-2 py-3">
                <CatRadioField
                  checked={selected === SELECTED_ADDRESS.ORIGINAL}
                  onBlRadioChange={() => setSelected(SELECTED_ADDRESS.ORIGINAL)}
                >
                  <div
                    className={cx(
                      'd-flex flex-column',
                      styles['avi-mobile__address']
                    )}
                  >
                    <p className="mb-0">{t('AVI_ORIGINAL_ADDRESS')}</p>
                    <Address locale={locale} address={currentAddress} />
                  </div>
                </CatRadioField>
              </div>
            </>
          ) : (
            <div
              className={cx(
                'd-flex flex-column px-2 py-3',
                styles['avi-mobile__address']
              )}
            >
              <p className="mb-0">{t('ES_RECOMMENDATIONS')}</p>
              <Address locale={locale} address={suggested} />
            </div>
          )}
        </div>
      </DrawerBody>
      <DrawerFooter className="d-flex gap-3">
        <CatButton variant="primary" fullwidth onClick={onContinueHandler}>
          {t('EF_CONTINUE')}
        </CatButton>
        <CatButton
          variant="ghost"
          fullwidth
          onClick={AVIMobileDrawerProps.closeDrawer}
        >
          {t('CANCEL')}
        </CatButton>
      </DrawerFooter>
    </Drawer>
  );
};

AVIMobileDrawer.propTypes = {
  isActive: PropTypes.bool,
  current: PropTypes.shape({
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zipCode: PropTypes.string,
    country: PropTypes.string
  }),
  onSubmit: PropTypes.func,
  errorSelector: PropTypes.string,
  errorPath: PropTypes.string,
  aviAddressData: PropTypes.shape({})
};

export default AVIMobileDrawer;
